import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../redux/hooks";
import { updateWidgetData, updateSiteContentRequest, selectPlainSiteContent } from "../redux/slices/siteContentSlice";
import { toggleNavEditMode } from "../redux/slices/navEditModeSlice";
import widgetRegistry from "../widgets/widgetRegistry";
import type { ClassicNavData } from "../widgets/nav/ClassicNav";

// Union type for all possible nav widget data types
type NavWidgetData = ClassicNavData; // Can be extended with BasicNavData | EssentialNavData | PrimeNavData

// Widget interface
interface NavWidget {
  type: string;
  data: NavWidgetData;
}

// Hook return interface with logical grouping
interface UseNavWidgetReturn {
  // Component data
  HeaderComponent: React.ComponentType<any> | null;
  headerData: NavWidgetData | undefined;
  
  // State
  isNavEditMode: boolean;
  
  // Loading states
  loading: {
    updateLoading: boolean;
    fetchLoading: boolean;
    saving: boolean;
  };
  
  // Error state
  error: string | null;
  
  // Actions
  actions: {
    toggleEditMode: () => void;
    update: (data: NavWidgetData) => void;
    save: () => void;
    retry: () => void;
  };
}

/**
 * Industry-standard custom hook for managing navigation widget functionality
 * Follows enterprise-grade patterns for performance, type safety, and maintainability
 */
export const useNavWidget = (): UseNavWidgetReturn => {
  const dispatch = useDispatch();
  
  // Consistent selector usage (useAppSelector)
  const siteContent = useAppSelector(selectPlainSiteContent);
  const { fetchLoading, updateLoading, error: siteContentError } = useAppSelector((state) => state?.siteContent);
  const isNavEditMode = useAppSelector((state) => state.navEditMode.isNavEditMode);

  // Refs for preventing stale closures and managing state
  const isSavingNav = useRef(false);
  const retryAttempts = useRef(0);
  const maxRetryAttempts = 3;
  
  // Local state with strong typing
  const [localNavData, setLocalNavData] = useState<NavWidgetData | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  // Performance optimization: Memoize repeated widget finding
  const headerWidget = useMemo((): NavWidget | undefined => {
    const widgets = siteContent?.widgets ?? [];
    return widgets.find((w: any) => w?.type?.toLowerCase().includes("nav")) as NavWidget | undefined;
  }, [siteContent?.widgets]);

  // Memoize component and data extraction
  const { HeaderComponent, headerData } = useMemo(() => {
    if (!headerWidget) {
      return { HeaderComponent: null, headerData: undefined };
    }

    const { data: navData, type: navType } = headerWidget;
    const HeaderComponent = widgetRegistry[navType as keyof typeof widgetRegistry] || null;
    
    // Use local data when in edit mode, otherwise use Redux data
    const displayData = isNavEditMode && localNavData ? localNavData : navData;

    return { HeaderComponent, headerData: displayData };
  }, [headerWidget, isNavEditMode, localNavData]);

  // Error handling: Monitor for save failures
  const combinedError = useMemo(() => {
    return localError || (siteContentError && isSavingNav.current ? String(siteContentError) : null);
  }, [localError, siteContentError]);

  // Auto-exit navigation edit mode after successful save
  useEffect(() => {
    if (isSavingNav.current && !updateLoading && !fetchLoading && !combinedError) {
      dispatch(toggleNavEditMode());
      isSavingNav.current = false;
      retryAttempts.current = 0;
      setLocalError(null);
    }
  }, [updateLoading, fetchLoading, combinedError, dispatch]);

  // Error handling: Reset saving state on error
  useEffect(() => {
    if (combinedError && isSavingNav.current) {
      isSavingNav.current = false;
      // Keep edit mode open on error for user to see and retry
    }
  }, [combinedError]);

  // Sync local nav data when Redux data changes or edit mode starts
  useEffect(() => {
    if (!headerWidget) return;

    if (isNavEditMode && !localNavData) {
      setLocalNavData(headerWidget.data);
    } else if (!isNavEditMode) {
      setLocalNavData(null);
      setLocalError(null);
    }
  }, [headerWidget, isNavEditMode, localNavData]);

  // Debounced save function to reduce backend load
  const debouncedSaveRef = useRef<number | undefined>(undefined);
  
  const performSave = useCallback(() => {
    if (!updateLoading && localNavData && headerWidget) {
      try {
        const widgets = siteContent?.widgets ?? [];
        const headerWidgetIndex = widgets.findIndex((w: any) => 
          w?.type?.toLowerCase().includes("nav")
        );
        
        if (headerWidgetIndex !== -1) {
          dispatch(updateWidgetData({ 
            index: headerWidgetIndex, 
            data: localNavData
          }));
          
          isSavingNav.current = true;
          setLocalError(null);
          dispatch(updateSiteContentRequest());
        } else {
          setLocalError("Navigation widget not found");
        }
      } catch (error) {
        setLocalError(`Save failed: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
  }, [dispatch, updateLoading, localNavData, headerWidget, siteContent?.widgets]);

  const handleSave = useCallback(() => {
    // Clear existing debounced call
    if (debouncedSaveRef.current) {
      clearTimeout(debouncedSaveRef.current);
    }
    
    // Immediate save for user action
    performSave();
  }, [performSave]);

  // Actions with proper typing and error handling
  const handleUpdate = useCallback((newData: NavWidgetData) => {
    if (isNavEditMode) {
      setLocalNavData(newData);
      setLocalError(null);
    }
  }, [isNavEditMode]);

  const handleToggleEditMode = useCallback(() => {
    if (isNavEditMode) {
      handleSave();
    } else {
      dispatch(toggleNavEditMode());
      setLocalError(null);
    }
  }, [isNavEditMode, handleSave, dispatch]);

  const handleRetry = useCallback(() => {
    if (retryAttempts.current < maxRetryAttempts) {
      retryAttempts.current++;
      setLocalError(null);
      performSave();
    } else {
      setLocalError("Maximum retry attempts reached. Please try again later.");
    }
  }, [performSave]);

  // Cleanup debounced save on unmount
  useEffect(() => {
    return () => {
      if (debouncedSaveRef.current) {
        clearTimeout(debouncedSaveRef.current);
      }
    };
  }, []);

  // Return with logical grouping and strong typing
  return {
    HeaderComponent,
    headerData,
    isNavEditMode,
    loading: {
      updateLoading,
      fetchLoading,
      saving: isSavingNav.current
    },
    error: combinedError,
    actions: {
      toggleEditMode: handleToggleEditMode,
      update: handleUpdate,
      save: handleSave,
      retry: handleRetry
    }
  };
};
